/**
 * The user settings context object, its hook and the client's key
 * registry, apart from the provider (UserSettingsContext.js), so the
 * components that only READ user settings (the unit margin tile) do not
 * pull in the API client. With no provider above them they read the
 * built-in defaults.
 */
import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    COLUMN_LAYOUT_KEY, DEFAULT_COLUMN_LAYOUT, normalizeColumnLayout, rebaseColumnLayout,
} from '../utils/inventoryColumns';

// The keys this client knows, their defaults and how a stored value reads.
export const KNOWN = Object.freeze({
    [COLUMN_LAYOUT_KEY]: {
        defaultValue: DEFAULT_COLUMN_LAYOUT,
        normalize: normalizeColumnLayout,
        // Re-apply a change (start → next) to a newer starting value (F-1).
        rebase: rebaseColumnLayout,
        failureText: "Column layout couldn't be saved. It will apply until you leave the page.",
    },
});
export const GENERIC_FAILURE = "Your settings couldn't be saved. They will apply until you leave the page.";

export const DEFAULT_USER_VALUES = Object.freeze(Object.fromEntries(
    Object.entries(KNOWN).map(([key, def]) => [key, def.normalize(def.defaultValue)]),
));

export const resolveValue = (key, value) => {
    const def = KNOWN[key];
    if (!def) return value;
    return def.normalize(value ?? def.defaultValue);
};

export const resolveValues = (values, base = DEFAULT_USER_VALUES) => {
    const out = { ...base };
    Object.entries(values || {}).forEach(([key, value]) => {
        if (KNOWN[key] && value !== null && value !== undefined) out[key] = resolveValue(key, value);
    });
    return out;
};

export const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const customizedKeys = (values, base) => Object.keys(KNOWN).filter((key) => !same(values[key], base[key]));

export const presetOf = (data) => {
    const p = data?.preset;
    if (!p || typeof p !== 'object' || !p.id) return null;
    return {
        id: p.id, name: p.name || '', version: p.version ?? null, source: p.source ?? null,
    };
};

export const NO_LIST = Object.freeze([]);

export const NO_PROVIDER = Object.freeze({
    values: DEFAULT_USER_VALUES,
    base: DEFAULT_USER_VALUES,
    customized: NO_LIST,
    preset: null,
    presetsAvailable: NO_LIST,
    margin: null,
    loading: false,
    loaded: false,
    canSave: false,
    version: 0,
    failure: null,
    save: () => {},
    choosePreset: async () => false,
    savePreferences: async () => false,
    reload: async () => null,
    sync: async () => null,
});

export const UserSettingsContext = createContext(NO_PROVIDER);

/**
 * { values, base, customized, preset, presetsAvailable, margin, loading,
 *   loaded, canSave, version, failure, save, choosePreset,
 *   savePreferences, reload, sync }.
 */
export function useUserSettings() {
    return useContext(UserSettingsContext);
}

/** For tests and previews: fixed user settings over the no-provider defaults. */
export function StaticUserSettings({ value = {}, children }) {
    const merged = useMemo(() => ({ ...NO_PROVIDER, ...value }), [value]);
    return <UserSettingsContext.Provider value={merged}>{children}</UserSettingsContext.Provider>;
}

StaticUserSettings.propTypes = { value: PropTypes.object, children: PropTypes.node };

